import { create } from 'zustand';
import { storageApi } from '../utils/api';
import { getGitHubToken } from '../utils/indexedDB';
import toast from 'react-hot-toast';

// 存储状态管理
export const useStorageStore = create((set, get) => ({
  // 状态
  kvUsage: 0,
  d1Usage: 0,
  isGithubLinked: false,
  githubOwner: '',
  githubRepo: '',
  isLoading: false,
  githubQuota: { remaining: 0, limit: 5000, resetAt: '', isSafe: true },
  
  // 计算属性
  kvPercentage: 0,
  d1Percentage: 0,
  isKVWarning: false,
  isKVCritical: false,
  isD1Warning: false,
  isD1Critical: false,
  
  // 获取存储状态
  fetchStatus: async () => {
    try {
      const data = await storageApi.getStatus();
      
      const kvPercentage = data.kv?.percentage || 0;
      const isKVWarning = kvPercentage > 80;
      const isKVCritical = kvPercentage > 95;
      
      const d1Percentage = data.d1?.percentage || 0;
      const isD1Warning = d1Percentage > 75;
      const isD1Critical = d1Percentage > 90;
      
      set({
        kvUsage: data.kv?.used || 0,
        d1Usage: data.d1?.used || 0,
        kvPercentage,
        isKVWarning,
        isKVCritical,
        d1Percentage,
        isD1Warning,
        isD1Critical,
        isGithubLinked: data.github?.enabled || false,
        githubOwner: data.github?.owner || '',
        githubRepo: data.github?.repo || '',
      });
      
      // 触发全局预警
      if (isKVWarning && !data.github?.enabled) {
        toast.warning('KV 空间告警：请尽快绑定 GitHub 开启无限存储', {
          duration: 5000,
          icon: '⚠️',
        });
      }
      
      return data;
    } catch (error) {
      console.error('获取存储状态失败:', error);
      toast.error('获取存储状态失败');
      return null;
    }
  },
  
  // 检查是否需要 GitHub 存储
  shouldUseGitHub: () => {
    const { kvPercentage, isGithubLinked } = get();
    return kvPercentage > 80 || !isGithubLinked;
  },
  
  // 上传文件（自动分流）
  uploadFile: async (file, path) => {
    const { kvPercentage, isGithubLinked } = get();
    
    // 如果 KV 超过 80% 且未绑定 GitHub，拦截并跳转
    if (kvPercentage > 80 && !isGithubLinked) {
      toast.error('存储空间已满，请先绑定 GitHub');
      throw new Error('STORAGE_FULL');
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path || file.name);
    
    // 从 IndexedDB 获取 GitHub Token
    const token = await getGitHubToken();
    if (token) {
      formData.append('token', token);
    }
    
    const result = await storageApi.uploadFile(formData);
    
    // 刷新状态
    await get().fetchStatus();
    
    return result;
  },
  
  // 验证 GitHub Token
  verifyGitHubToken: async (token) => {
    set({ isLoading: true });
    try {
      const result = await storageApi.verifyGitHubToken(token);
      set({ isLoading: false });
      return result;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
  
  // 创建 GitHub 仓库
  createGitHubRepo: async (repoName, token) => {
    set({ isLoading: true });
    try {
      const result = await storageApi.createGitHubRepo(repoName, token);
      await get().fetchStatus();
      set({ isLoading: false });
      return result;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
  
  // 获取提交历史
  getCommitHistory: async () => {
    try {
      const data = await storageApi.getCommitHistory();
      return data.commits || [];
    } catch (error) {
      console.error('获取提交历史失败:', error);
      return [];
    }
  },
  
  // 获取 GitHub 配额
  fetchGithubQuota: async () => {
    try {
      const data = await storageApi.getGithubQuota();
      const isSafe = data.remaining >= 100;
      set({
        githubQuota: {
          remaining: data.remaining || 0,
          limit: data.limit || 5000,
          resetAt: data.resetAt || '',
          isSafe,
        },
      });
      return data;
    } catch (error) {
      console.error('获取 GitHub 配额失败:', error);
      return null;
    }
  },
}));
